import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Star } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, Pill, fmtNum, IataBadge } from "../components/common";
import { Pagination } from "./Airports";

const TYPE_LABELS = {
  large_airport: "Large", medium_airport: "Medium", small_airport: "Small",
  heliport: "Heliport", seaplane_base: "Seaplane", closed: "Closed",
};

export default function AirportsDirectory() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setRegion("");
    if (!country) { setRegions([]); return; }
    api.get("/airport-regions", { params: { country } }).then((r) => setRegions(r.data.items)).catch(() => setRegions([]));
  }, [country]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/airports", { params: { q, type, country, region, page, limit: 24 } });
      setData(data);
    } finally { setLoading(false); }
  }, [q, type, country, region, page]);

  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setPage(1); }, [q, type, country, region]);
  useEffect(() => { setParams(q ? { q } : {}, { replace: true }); }, [q, setParams]);

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div data-testid="airports-directory-page">
      <PageHeader eyebrow="Directory" title="All Airports A–Z"
        subtitle="Search 85,000+ airports worldwide — filter by country, state and type, and open any profile for terminals, routes and serving airlines." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Link to="/airports" data-testid="back-to-popular-airports-link"
          className="inline-flex items-center gap-1.5 text-sm font-700 text-primary hover:underline mb-4">
          <Star className="w-4 h-4" /> Back to Popular Airports
        </Link>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <input data-testid="airports-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={Boolean(params.get("q"))}
            placeholder="Search by name, city or IATA code…"
            className="lg:col-span-1 border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-colors" />
          <select data-testid="airports-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
            className="border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary">
            <option value="">All countries</option>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="airports-region-filter" value={region} onChange={(e) => setRegion(e.target.value)} disabled={!country}
            className="border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary disabled:opacity-50">
            <option value="">{country ? "All states / regions" : "Select a country first"}</option>
            {regions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
          <select data-testid="airports-type-filter" value={type} onChange={(e) => setType(e.target.value)}
            className="border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary">
            <option value="">All types</option>
            <option value="large_airport">Large airports</option>
            <option value="medium_airport">Medium airports</option>
            <option value="small_airport">Small airports</option>
            <option value="heliport">Heliports</option>
          </select>
        </div>

        {loading ? <Spinner /> : (
          <>
            <div className="text-sm text-muted-foreground mb-3 font-mono">{fmtNum(data?.total)} results · alphabetical</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data?.items.map((a, index) => (
                <Link key={`${a.id}-${a.iata_code || a.icao_code || index}-${index}`} to={`/airports/${a.iata_code || a.id}`} data-testid={`airport-card-${a.id}-${index}`}
                  className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                  <IataBadge code={a.iata_code || a.icao_code || "—"} name={a.name} className="h-28" />
                  <div className="p-5">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />{a.municipality || "—"}, {a.iso_country}
                    </div>
                    <div className="mt-3"><Pill>{TYPE_LABELS[a.type] || a.type}</Pill></div>
                  </div>
                </Link>
              ))}
            </div>
            {data?.items.length === 0 && <div className="py-16 text-center text-muted-foreground">No airports found.</div>}
            {totalPages > 1 && <Pagination page={page} totalPages={totalPages} setPage={setPage} />}
          </>
        )}
      </div>
    </div>
  );
}
